import { useEffect, useState } from "react"
import { Link, NavLink } from "react-router-dom"
import { Menu, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { assetUrl } from "@/lib/asset-url"
import { env } from "@/lib/env"
import { Button } from "@/components/ui/button"
import { ContrastToggle } from "./contrast-toggle"
import { routes } from "@/lib/routes"

const NAV_ITEMS = [
  {
    to: routes.undergraduate,
    label: "Undergraduate Scholarship",
  },
  {
    to: routes.postgraduate,
    label: "Postgraduate Scholarship",
  },
] as const

/**
 * Figma / ENT header: logo left, text links with teal underline indicator,
 * portal login, contrast toggle. Cream sticky bar with scroll shadow.
 */
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const portalUrl = env.applicationPortalUrl

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false)
    }
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) setMobileOpen(false)
    }
    window.addEventListener("keydown", onKey)
    window.addEventListener("resize", onResize)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("resize", onResize)
    }
  }, [mobileOpen])

  return (
    <header
      className={cn(
        "sticky top-0 z-40 bg-surface-warm transition-shadow duration-500",
        scrolled && "shadow-[0_0_20px_0_rgba(0,0,0,0.1)]",
      )}
    >
      <div className="mx-auto flex min-h-[72px] max-w-[1200px] items-center justify-between gap-3 px-4 py-2 sm:min-h-[90px] sm:px-6 sm:py-0">
        <Link
          to={routes.home}
          className="inline-flex min-w-0 shrink items-center py-1 outline-none ring-ring/30 focus-visible:ring-2"
          aria-label="Reliance Foundation Scholarships — home"
          onClick={() => setMobileOpen(false)}
        >
          <img
            src={assetUrl("/assets/img/rf-scholarships-logo.png")}
            alt="Reliance Foundation Scholarship Logo"
            className="h-auto max-h-[48px] w-auto max-w-[137px] object-contain object-left sm:max-h-[60px]"
          />
        </Link>

        <nav
          aria-label="Primary"
          className="hidden min-w-0 flex-1 items-center justify-end gap-1 md:flex lg:gap-2"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rf-nav-link relative px-3 py-5 font-body text-[14px] font-semibold leading-none text-black outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring lg:px-4 lg:text-[15px]",
                  isActive && "is-active",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rf-nav-link relative px-3 py-5 font-body text-[14px] font-semibold leading-none text-black outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring lg:px-4 lg:text-[15px]"
            title="Opens in a new tab"
          >
            Application Portal Log-In
          </a>
          <ContrastToggle
            showLabel={false}
            className="ml-1 size-6 shrink-0 justify-center p-0"
          />
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 md:hidden">
          <ContrastToggle
            showLabel={false}
            className="size-9 justify-center rounded-full border border-border/60 bg-white/70 p-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border border-border/60 bg-white/70 text-brand-teal hover:bg-white"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          id="mobile-nav"
          aria-label="Primary"
          className="border-t border-[#f1f1f1] bg-white md:hidden"
        >
          <ul className="mx-auto flex max-w-[1200px] flex-col px-4 py-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to} className="border-b border-[#f1f1f1]">
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "block px-1 py-3 font-body text-sm font-semibold text-black",
                      isActive && "text-brand-teal",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            <li>
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-1 py-3 font-body text-sm font-semibold text-black"
                title="Opens in a new tab"
                onClick={() => setMobileOpen(false)}
              >
                Application Portal Log-In
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  )
}
